// Packages
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gql } from '@apollo/client';
import {
	usePermissionsModal_AssignPlannerPermissionsMutation,
	usePermissionsModal_RemoveAllPlannerPermissionsMutation,
	usePermissionsModal_GetAuthedUserLazyQuery,
} from '#types';
import type {
	PermissionsModalOrganizationFragment,
	PermissionsModalOrganizationForGetContractWhereFragment,
} from '#types';

// Markup
import { useToast } from '../../hooks/useToast';
import ConfirmPermissionsChangeModal from './ConfirmPermissionsChangeModal';
import SetPermissionsModal from './SetPermissionsModal';
import { PermissionsSettings } from './SetPermissionsModal/PermissionsToggles';
import { PermissionsToggles } from './SetPermissionsModal/PermissionsToggles';
import {
	FlattenedUser,
	getAuthedUserFlattenedUserFieldsFragment,
} from '../../types/user';
import { transformGetAuthedUserForRedux } from '../../redux/helpers';
import { updateUser } from '../../redux/actions';

/* OrganizationForGetContractWhere doesn't have child component fragments,
so we define it directly here with the fields needed by VendorProfile */
const permissionsModalOrganizationForGetContractWhereFragment = gql`
	fragment permissionsModalOrganizationForGetContractWhere on OrganizationForGetContractWhere {
		id
		image
		name
		services {
			name
			id
		}
	}
`;

const permissionsModalOrganizationFragment = gql`
	fragment permissionsModalOrganization on Organization {
		...setPermissionsModalVendor
	}
	${ SetPermissionsModal.fragments.setPermissionsModalVendorFragment }
`;

// Fragment for contact with clientUser and vendor data needed for PermissionsModal
export const permissionsModalContactFragment = gql`
	fragment permissionsModalContact on Contact {
		id
		vendor {
			...setPermissionsModalVendor
		}
		customer {
			clientUser {
				id
				...permissionsTogglesPermissions
				assignedPlanner {
					id
					organization {
						id
					}
				}
			}
		}
	}
	${ SetPermissionsModal.fragments.setPermissionsModalVendorFragment }
	${ PermissionsToggles.fragments.permissionsTogglesPermissionsFragment }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const assignPlannerPermissionsMutation = gql`
	mutation PermissionsModal_assignPlannerPermissions(
		$where: ContactWhereUniqueInput!
		$data: PermissionInput!
	) {
		assignPlannerPermissions(where: $where, data: $data) {
			id
		}
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const removeAllPlannerPermissionsMutation = gql`
	mutation PermissionsModal_removeAllPlannerPermissions(
		$where: ClientUserWhereUniqueInput!
	) {
		removeAllPlannerPermissions(where: $where)
	}
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getAuthedUserQuery = gql`
	query PermissionsModal_getAuthedUser($where: UserWhereUniqueInput!) {
		getAuthedUser(where: $where) {
			...getAuthedUserFlattenedUserFields
		}
	}
	${ getAuthedUserFlattenedUserFieldsFragment }
`;

export type VendorForPermissions =
	| PermissionsModalOrganizationFragment
	| PermissionsModalOrganizationForGetContractWhereFragment;

export type PermissionsModalProps = {
	clientUserId: string;
	contactId: string;
	vendor: VendorForPermissions;
	vendorWithPermissions?: VendorForPermissions;
	currentPermissions?: PermissionsSettings;
	showPaymentText?: boolean;
	onCancel: () => void;
	onConnect: (
		contactId: string,
		newPermissions: PermissionsSettings
	) => Promise<void>;
};

const PermissionsModal = ( {
	clientUserId,
	contactId,
	vendor,
	vendorWithPermissions,
	currentPermissions,
	showPaymentText,
	onCancel,
	onConnect,
}: PermissionsModalProps ): React.JSX.Element => {
	const dispatch = useDispatch();
	const user: FlattenedUser = useSelector( ( state: any ) => state?.user );
	const { showError, showSuccess } = useToast();
	const [ confirmModalVisible, showConfirmModal ] = useState( false );
	const [ isLoading, setIsLoading ] = useState( false );
	const [ permissions, setPermissions ] = useState<PermissionsSettings>(
		vendorWithPermissions &&
			currentPermissions &&
			vendorWithPermissions.id === vendor.id
			? currentPermissions
			: {
				plannerOrgHasConnectionPermissions: false,
				plannerOrgCanSignContracts: false,
				plannerOrgCanPayInvoices: false,
				plannerOrgHasDocumentsPermissions: false,
			  }
	);

	const [ getAuthedUser ] = usePermissionsModal_GetAuthedUserLazyQuery();
	const [ assignPlannerPermissions ] =
		usePermissionsModal_AssignPlannerPermissionsMutation( {
			onError: ( error: Error ) => {
				showError( error.message );
			},
			refetchQueries: [ 'organizationDetail_getContactWhere' ],
		} );

	const [ removeAllPlannerPermissions ] =
		usePermissionsModal_RemoveAllPlannerPermissionsMutation( {
			onError: ( error: Error ) => {
				showError( error.message );
			},
			refetchQueries: [ 'organizationDetail_getContactWhere' ],
		} );

	const somePermissionsGranted = Object.values( permissions ).some(
		( v ) => v === true
	);

	const takePermissionsAction = useCallback( async () => {
		if (
			vendorWithPermissions &&
			vendorWithPermissions.id !== vendor.id &&
			!confirmModalVisible
		) {
			showConfirmModal( true );
		} else {
			setIsLoading( true );
			try {
				if ( somePermissionsGranted ) {
					await assignPlannerPermissions( {
						variables: {
							where: { id: contactId },
							data: permissions,
						},
					} );
					showSuccess( 'Permissions updated' );
				} else {
					await removeAllPlannerPermissions( {
						variables: {
							where: { id: clientUserId },
						},
					} );
					showSuccess( 'Permissions removed' );
				}
				await onConnect( contactId, permissions );

				// Refetch the user to update permissions in redux
				if ( user?.isLoggedIn && user.user.id ) {
					const response = await getAuthedUser( {
						variables: { where: { id: user.user.id } },
					} );
					if ( response.error ) {
						showError( response.error );
					} else if ( response.data?.getAuthedUser ) {
						const userForRedux = transformGetAuthedUserForRedux(
							response.data.getAuthedUser
						);
						if ( userForRedux ) {
							dispatch( updateUser( userForRedux ) );
						}
					}
				}

				// eslint-disable-next-line @typescript-eslint/no-unused-vars
			} catch ( error ) {
				// Error is already handled by onError callback in useMutation
			} finally {
				setIsLoading( false );
			}
		}
	}, [
		somePermissionsGranted,
		vendorWithPermissions,
		vendor.id,
		confirmModalVisible,
		onConnect,
		contactId,
		permissions,
		clientUserId,
		showSuccess,
		assignPlannerPermissions,
		removeAllPlannerPermissions,
		user,
		getAuthedUser,
		showError,
		dispatch,
	] );

	return confirmModalVisible ? (
		<ConfirmPermissionsChangeModal
			open={ confirmModalVisible }
			vendor={ vendor }
			vendorWithPermissions={ vendorWithPermissions }
			isLoading={ isLoading }
			onCancel={ async () => {
				showConfirmModal( false );
				onCancel();
			} }
			onConfirm={ async () => {
				showConfirmModal( false );
				await takePermissionsAction();
			} }
		/>
	) : (
		<SetPermissionsModal
			open={ !confirmModalVisible }
			showPaymentText={ showPaymentText || false }
			vendor={ vendor }
			currentPermissions={ currentPermissions }
			permissions={ permissions }
			setPermissions={ setPermissions }
			isLoading={ isLoading }
			onCancel={ onCancel }
			onConnect={ takePermissionsAction }
		/>
	);
};

PermissionsModal.fragments = {
	permissionsModalOrganizationFragment,
	permissionsModalOrganizationForGetContractWhereFragment,
	permissionsModalContactFragment,
};

export default PermissionsModal;
